import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LeaveMessageForm.module.css';
import { BackButton } from 'components/Map/Components/BackButton';
import { AnimatedButton } from 'components/Map/AnimatedButton';

interface ContactInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

interface LeaveMessageFormProps {
  onBack: () => void;
  onSubmit: (message: string, contactInfo: ContactInfo) => void;
}

export const LeaveMessageForm: React.FC<LeaveMessageFormProps> = ({ onBack, onSubmit }) => {
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = t('First name is required');
    }

    if (!lastName.trim()) {
      newErrors.lastName = t('Last name is required');
    }

    if (!email.trim()) {
      newErrors.email = t('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('Please enter a valid email');
    }

    if (!message.trim()) {
      newErrors.message = t('Message is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const contactInfo: ContactInfo = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: phone || undefined,
      };

      await onSubmit(message, contactInfo);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error submitting message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className={styles.formView}>
        <div className={styles.header}>
          <BackButton
            onClick={onBack}
            headerContent={<h2 className={styles.title}>{t('Leave a Message')}</h2>}
          />
        </div>
        <div className={styles.successMessage}>
          <img
            src="/static/images/map/checkmark.svg"
            className={styles.successIcon}
            alt="success"
          />
          <h3 className={styles.successTitle}>{t('Thanks for your message!')}</h3>
          <p className={styles.successText}>{t("We'll get back to you soon.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formView}>
      <div className={styles.header}>
        <BackButton
          onClick={onBack}
          headerContent={<h2 className={styles.title}>{t('Leave a Message')}</h2>}
        />
      </div>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          {i18n.language === 'ja-JP' ? (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="lastName" className={styles.label}>
                  {t('Last Name')} <span className={styles.required}>*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  className={styles.input}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.lastName && <span className={styles.error}>{errors.lastName}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="firstName" className={styles.label}>
                  {t('First Name')} <span className={styles.required}>*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  className={styles.input}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.firstName && <span className={styles.error}>{errors.firstName}</span>}
              </div>
            </>
          ) : (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="firstName" className={styles.label}>
                  {t('First Name')} <span className={styles.required}>*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  className={styles.input}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.firstName && <span className={styles.error}>{errors.firstName}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="lastName" className={styles.label}>
                  {t('Last Name')} <span className={styles.required}>*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  className={styles.input}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.lastName && <span className={styles.error}>{errors.lastName}</span>}
              </div>
            </>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.label}>
            {t('Email')} <span className={styles.required}>*</span>
          </label>
          <input
            id="email"
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
          />
          {errors.email && <span className={styles.error}>{errors.email}</span>}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="phone" className={styles.label}>
            {t('Phone')} <span className={styles.optional}>({t('optional')})</span>
          </label>
          <input
            id="phone"
            type="tel"
            className={styles.input}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="message" className={styles.label}>
            {t('Message')} <span className={styles.required}>*</span>
          </label>
          <textarea
            id="message"
            className={styles.textarea}
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSubmitting}
          />
          {errors.message && <span className={styles.error}>{errors.message}</span>}
        </div>

        <AnimatedButton
          type="submit"
          className={styles.submitButton}
          clickedClassName={styles.clicked}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('Submitting...') : t('Submit')}
        </AnimatedButton>
      </form>
    </div>
  );
};
